import {
  getModalManager,
  type ModalShowConfig,
} from "@UI/ModalManager/index.js";
import {
  createList,
  type ListAPI,
  type ListItemProps,
} from "@UI/List/index.js";
import type { PageSummary } from "@/types/project-types";
import "./PagesBrowser.scss"; // co-located styles [[memory:8094583]]

export interface PagesBrowserConfig {
  readonly pages: readonly PageSummary[];
  readonly selectedPageId?: string | null;
  readonly anchor?: ModalShowConfig["anchor"];
  readonly width?: number | string;
  readonly offset?: { x: number; y: number };
  readonly onSelect?: (pageId: string) => void;
  readonly onAddNew?: () => void;
  readonly onSearchChange?: (query: string) => void;
}

export interface PagesBrowserAPI {
  readonly getElement: () => HTMLElement;
  readonly show: () => void;
  readonly hide: () => void;
  readonly destroy: () => void;
}

const iconForPage = (page: PageSummary): string => {
  switch (page.type) {
    case "home":
      return "page_home";
    case "slug":
      return "page_slug";
    case "component":
    default:
      return "tools_component";
  }
};

export const createPagesBrowser = (
  config: PagesBrowserConfig
): PagesBrowserAPI => {
  const {
    pages,
    selectedPageId = null,
    width = 510,
    anchor = "bottom-center",
    offset = { x: 0, y: 100 },
    onSelect,
    onAddNew,
    onSearchChange,
  } = config;

  const modal = getModalManager();

  const container = document.createElement("div");
  container.className = "pages-browser";

  const listConfig: any = {
    items: pages,
    selectedId: selectedPageId,
    mapItem: (p: PageSummary): ListItemProps => ({
      id: p.id,
      leftIcon: iconForPage(p),
      primaryLabel: p.name,
    }),
    onSelect: (id: string) => {
      onSelect?.(id);
      modal.hide();
    },
  };

  if (onSearchChange || onAddNew) {
    listConfig.header = {
      searchable: true,
      ...(onSearchChange && { onSearchChange }),
      ...(onAddNew && { onAddNew }),
    } satisfies import("@UI/List/index.js").ListHeaderConfig;
  }

  const list: ListAPI = createList<PageSummary>(listConfig);

  container.appendChild(list.getElement());

  const show = (): void => {
    const cfg: ModalShowConfig = {
      content: container,
      anchor,
      offset,
      width,
      closeOnBackdrop: true,
    };
    modal.show(cfg);
    requestAnimationFrame(() => list.focusFirst());
  };

  const hide = (): void => {
    if (modal.getCurrentContent() === container) modal.hide();
  };

  const destroy = (): void => {
    hide();
    list.destroy();
    container.remove();
  };

  return { getElement: () => container, show, hide, destroy };
};
